import { useEffect, useMemo } from 'react';
import { useBootstrapQuery } from '../core/queries/bootstrap';
import { queryClient } from '../core/queryClient';
import { storage } from '../platform';
import { useConsentStore } from '../store/consentStore';
import { sanitizeBootstrapAccountData } from '../core/api/consentPolicy';

const BOOTSTRAP_STORAGE_KEY = 'nb.bootstrap.v1';
const BOOTSTRAP_SKELETON_MAX_AGE_MS = 2 * 60 * 1000;

function readBootstrapSnapshot() {
  try {
    const raw = storage.get(BOOTSTRAP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data) return null;
    return {
      data: parsed.data,
      savedAt: Number(parsed.savedAt || 0),
    };
  } catch {
    return null;
  }
}

export function readCachedBootstrap() {
  return readBootstrapSnapshot()?.data ?? null;
}

export function readFreshCachedBootstrap(maxAgeMs = BOOTSTRAP_SKELETON_MAX_AGE_MS) {
  const snapshot = readBootstrapSnapshot();
  if (!snapshot?.data) return null;
  if (!snapshot.savedAt) return null;
  if (Date.now() - snapshot.savedAt > maxAgeMs) return null;
  return snapshot.data;
}

export function __resetBootstrapCacheForTests() {
  queryClient.removeQueries({ queryKey: ['bootstrap'] });
  storage.remove(BOOTSTRAP_STORAGE_KEY);
}

function normalizeSkeletonBootstrap(source) {
  if (!source) return null;
  return {
    ...source,
    heroBanners: Array.isArray(source.heroBanners) ? source.heroBanners : [],
  };
}

export default function useBootstrap() {
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const query = useBootstrapQuery();
  const cachedData = useMemo(() => {
    if (query.data) {
      return normalizeSkeletonBootstrap(
        sanitizeBootstrapAccountData(query.data, consentRequired),
      );
    }
    return normalizeSkeletonBootstrap(
      sanitizeBootstrapAccountData(
        readFreshCachedBootstrap() ?? readCachedBootstrap(),
        consentRequired,
      ),
    );
  }, [consentRequired, query.data]);

  useEffect(() => {
    if (!query.data) return;
    storage.set(
      BOOTSTRAP_STORAGE_KEY,
      JSON.stringify({
        data: query.data,
        savedAt: Date.now(),
      }),
    );
  }, [query.data]);

  return {
    data: sanitizeBootstrapAccountData(query.data, consentRequired) ?? null,
    skeletonData: cachedData,
    loading: query.isLoading,
    fetching: query.isFetching,
    error: query.error ?? null,
    isError: query.isError,
    refetch: query.refetch,
  };
}
